import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../../features/auth/store'
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload'
import { useToast } from '../../../shared/components/toast/useToast'
import { storeConfigApi, type BrandAssets } from '../../../features/storefront-onboarding/api/storeConfigApi'
import { KeyedImageSlot } from '../../../features/storefront-onboarding/components/KeyedImageSlot'
import { useOccasions } from '../../../features/products/hooks/useMetaData'
import { metaDataApi, type OccasionPayload } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

/**
 * Occasions configuration (PE-2104, rebuilt on the real CRUD).
 *
 * TWO halves, deliberately on one tab:
 *
 * 1. The TAXONOMY — product-service's `occasions` resource, proxied by osprey-api as
 *    `/occasions` (GET/POST/PATCH/DELETE). This is the source of truth for what occasions
 *    exist; `tenantId` is merged server-side from the `x-tenant-id` header the shared fetch
 *    wrapper already sends. GET answers with a BARE ARRAY — see `toPage` in metaDataApi.
 *
 * 2. The TILES — `{ label, image }` rows on the store-config `brandAssets.occasions` blob.
 *    This predates the CRUD and is NOT dead: the storefront reads it at runtime and matches
 *    each tile to the taxonomy BY LABEL, so a tile's label must equal an occasion's name.
 *    Deleting this half would strip imagery from every live storefront.
 */
export const OccasionsTab = () => {
  return (
    <div className="flex flex-col gap-6">
      <OccasionsCrud />
      <OccasionTiles />
    </div>
  )
}

/** Half 1 — the real `/occasions` taxonomy, mirroring CategoriesTab. */
const OccasionsCrud = () => {
  const { data, isLoading } = useOccasions()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['occasions'] })

  const createMutation = useMutation({
    mutationFn: (payload: OccasionPayload) => metaDataApi.createOccasion(payload),
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: OccasionPayload }) =>
      metaDataApi.updateOccasion(id, payload),
    onSuccess: () => {
      invalidate()
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteOccasion(id),
    onSuccess: invalidate,
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'slug',
      label: 'Slug',
      render: (item: Record<string, unknown>) => (item.slug as string) || '—',
    },
    {
      key: 'description',
      label: 'Description',
      render: (item: Record<string, unknown>) => (
        <span className="text-muted-foreground">{(item.description as string) || '—'}</span>
      ),
    },
  ]

  const renderAddForm = (onDone: () => void) => (
    <OccasionForm
      isLoading={createMutation.isPending}
      submitLabel="Save"
      onSubmit={async (payload) => {
        await createMutation.mutateAsync(payload)
        onDone()
      }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <OccasionForm
      defaultName={item.name as string}
      defaultDescription={(item.description as string) ?? ''}
      isLoading={updateMutation.isPending}
      submitLabel="Update"
      onSubmit={(payload) => updateMutation.mutateAsync({ id: item.id as string, payload })}
      onCancel={onDone}
    />
  )

  return (
    <MetaTable
      title="Occasions"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Occasion"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const OccasionForm = ({
  defaultName = '',
  defaultDescription = '',
  isLoading,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  defaultName?: string
  defaultDescription?: string
  isLoading: boolean
  submitLabel: string
  onSubmit: (payload: OccasionPayload) => Promise<unknown>
  onCancel: () => void
}) => {
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState(defaultDescription)

  // `description` is optional on both DTOs, but OMITTING it is not how you clear one:
  // product-service `OccasionsService.update` only writes the column when
  // `dto.description !== undefined`, so an omitted field leaves the old text in place
  // forever. An empty STRING is the clear (the column is `@IsString()`, so `null` would
  // fail validation). Send it whenever the admin typed something OR the row already had a
  // description they have now blanked; omit it only when creating with an empty box.
  const submit = () => {
    const next = description.trim()
    const payload: OccasionPayload = { name: name.trim() }
    if (next || defaultDescription) payload.description = next
    return onSubmit(payload)
  }

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input
          label="Name"
          placeholder="Occasion name (e.g. Wedding)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-[38px]"
        />
      </div>
      <div className="flex-1">
        <Input
          label="Description"
          placeholder="Optional"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="h-[38px]"
        />
      </div>
      <Button
        size="sm"
        onClick={submit}
        disabled={!name.trim() || isLoading}
        loading={isLoading}
        className="h-[38px]"
      >
        {submitLabel}
      </Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">
        Cancel
      </Button>
    </div>
  )
}

/**
 * Half 2 — the shipped `brandAssets.occasions` image tiles, unchanged. Scoped to the merchant
 * selected in the top-bar tenant switcher (`selectedTenantId`), same storeConfigApi +
 * media-upload path as the Brand Assets panel.
 */
const OccasionTiles = () => {
  const merchantId = useAuthStore((s) => s.selectedTenantId)
  const { upload } = useMediaUpload()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [assets, setAssets] = useState<BrandAssets>({})
  const [busy, setBusy] = useState<string | null>(null)
  const hydrated = useRef(false)

  const storeConfigKey = ['store-config', merchantId] as const
  const { data: storeConfig, isLoading } = useQuery({
    queryKey: storeConfigKey,
    queryFn: () => storeConfigApi.get(merchantId as string),
    staleTime: 60 * 1000,
    enabled: !!merchantId,
  })

  // Seed the editor from the server once; local state is authoritative for this session after.
  useEffect(() => {
    if (storeConfig && !hydrated.current) {
      setAssets(storeConfig.brandAssets ?? {})
      hydrated.current = true
    }
  }, [storeConfig])

  const persistMutation = useMutation({
    mutationFn: (next: BrandAssets) =>
      storeConfigApi.updateBusinessProfile(merchantId as string, { brandAssets: next }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: storeConfigKey }),
  })

  const persist = useCallback(
    async (next: BrandAssets, successMsg: string) => {
      setAssets(next)
      try {
        await persistMutation.mutateAsync(next)
        toast.success('Saved', successMsg)
      } catch (err) {
        const msg = (err as { message?: string })?.message ?? 'could not save to store config'
        toast.error('Uploaded, not saved', `On GCS, but ${msg}.`)
      }
    },
    [persistMutation, toast],
  )

  const setOccasionImage = useCallback(
    async (label: string, file: File | undefined) => {
      if (!file || !label.trim() || !merchantId) return
      setBusy(label)
      try {
        const uploaded = await upload(file, 'rack-and-box', { tenantId: merchantId, tenantType: 'APP' })
        const rows = [...(assets.occasions ?? [])]
        const idx = rows.findIndex((r) => r.label.trim().toLowerCase() === label.trim().toLowerCase())
        if (idx >= 0) rows[idx] = { label: rows[idx].label, image: uploaded.url }
        else rows.push({ label: label.trim(), image: uploaded.url })
        await persist({ ...assets, occasions: rows }, `${label} tile saved`)
      } catch (err) {
        toast.error('Upload failed', (err as { message?: string })?.message ?? 'Upload failed')
      } finally {
        setBusy(null)
      }
    },
    [assets, upload, persist, toast, merchantId],
  )

  const removeOccasion = useCallback(
    (label: string) => {
      const rows = (assets.occasions ?? []).filter(
        (r) => r.label.trim().toLowerCase() !== label.trim().toLowerCase(),
      )
      const next = { ...assets }
      if (rows.length) next.occasions = rows
      else delete next.occasions
      void persist(next, `${label} tile removed`)
    },
    [assets, persist],
  )

  if (!merchantId) {
    return (
      <p className="text-[13px] text-muted-foreground">
        Select a merchant from the top-bar switcher to upload occasion tile images.
      </p>
    )
  }

  if (isLoading) {
    return <p className="text-[13px] text-muted-foreground">Loading occasion tiles…</p>
  }

  return (
    <div className="max-w-2xl">
      <KeyedImageSlot
        title="Occasion tiles"
        hint="Image per occasion. The label must match an occasion name in the table above (e.g. Wedding, Birthday) — the storefront pairs tiles to the taxonomy by label."
        placeholder="Occasion label (e.g. Wedding)"
        rows={assets.occasions ?? []}
        busyLabel={busy}
        onUpload={setOccasionImage}
        onRemove={removeOccasion}
      />
    </div>
  )
}
